/**
 * The difference between a blank page and a bug report.
 *
 * React unmounts the whole tree when a render or an effect throws, so a single bad assumption
 * about the shape of the engine's output takes the entire interface with it - no message, no
 * console line anybody thinks to open, just white. That happened: the result types were
 * written from assumption rather than from a committed corpus baseline, and `undefined.forEach`
 * inside an effect blanked the application on the first real conversion.
 *
 * This does not prevent that. It makes it say what it was.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The component stack is the useful half - it names which part of the tree threw, which
    // the message alone rarely does.
    console.error("The interface stopped:", error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <main className="crash" role="alert">
        <div className="crash__card">
          <h1 className="crash__title">The interface stopped</h1>
          <p className="crash__body">
            Something in the page threw and React unmounted it. Your conversion is not lost -
            jobs run on the server and finished results are kept.
          </p>
          <pre className="crash__detail">{error.message}</pre>
          <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
            Reload
          </button>
          <p className="crash__hint">
            The full stack is in the browser console. Worth reporting with the plan that
            triggered it.
          </p>
        </div>
      </main>
    );
  }
}
